import { useState, useEffect, useRef, useCallback } from 'react';
import { searchCustomers } from 'services/unifiedCustomersSearch';
import { getOrCreateToken } from 'services/qrService';

const debounce = (fn, wait) => {
  let t;
  return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
};

// Búsqueda de alumno por nombre/apellido/RUT + resolución a token — mismo
// contrato que usaba ManualScanner.js (pantalla completa de búsqueda) antes
// de esta extracción, ahora compartido también con ConsultaSearchBox (el
// buscador inline que aparece junto al switch Entrada/Salida al tocar el
// botón redondo de Consulta) para no duplicar el debounce ni el manejo de
// getOrCreateToken en dos lugares.
//
// `active` resetea term/results/error/resolving cada vez que pasa a true —
// útil para un consumidor que se queda montado y solo alterna esta bandera
// (como ManualScanner, ligado a phase==='scanning'); un consumidor que en
// cambio se desmonta entero al terminar (como ConsultaSearchBox) ya parte
// limpio por el propio useState inicial, así que pasar `true` fijo es
// suficiente ahí.
export default function useManualStudentSearch(active, onResolved) {
  const [term, setTerm]           = useState('');
  const [results, setResults]     = useState([]);
  const [searching, setSearching] = useState(false);
  const [resolving, setResolving] = useState(null); // usuario mientras se genera su token
  const [error, setError]         = useState(null);
  const lastAttemptRef = useRef(null); // para "Reintentar" tras un fallo de getOrCreateToken

  const runSearch = useCallback(debounce(async (q) => {
    try { setResults(await searchCustomers(q, 8)); }
    catch { setResults([]); }
    finally { setSearching(false); }
  }, 300), []);

  useEffect(() => {
    if (!term.trim()) { setResults([]); setSearching(false); return; }
    setSearching(true);
    runSearch(term);
  }, [term, runSearch]);

  useEffect(() => {
    if (active) { setTerm(''); setResults([]); setError(null); setResolving(null); }
  }, [active]);

  const pick = useCallback(async (user) => {
    if (!user.id) { setError('Este alumno no tiene cuenta asociada — no se puede generar su código.'); return; }
    lastAttemptRef.current = user;
    setError(null);
    setResolving(user);
    try {
      const res = await getOrCreateToken(user.id, user.username);
      onResolved(res.data.token);
    } catch (e) {
      setError(e?.response?.data?.message ?? 'No se pudo generar el código para este alumno.');
      setResolving(null);
    }
  }, [onResolved]);

  const retry = useCallback(() => {
    if (lastAttemptRef.current) pick(lastAttemptRef.current);
  }, [pick]);

  return { term, setTerm, results, searching, resolving, error, pick, retry };
}
